import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Select, Space, Input, Typography, theme } from 'antd';
import { ArrowRightOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { DateRangeFilter } from '../../../components/DateRangeFilter';
import { useVisitasList, useVisitasStats } from '../api/useVisitas';
import { useUsersList } from '../../users/api/useUsers';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { VisitaResultadoTag } from './VisitaResultadoTag';
import { VisitaCreateModal } from './VisitaCreateModal';
import { CLIENTE_TABS } from '../../clientes/cliente-tabs';
import {
  Permission,
  RESULTADO_VISITA,
  type DateOnlyString,
  type ResultadoVisita,
  type VisitaCobroResponse,
} from '@aid/shared';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';
import { VisitasKpiSummary } from './VisitasKpiSummary';

const { Text } = Typography;

const INTERACTIVE_ROW_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  '[data-row-interactive]',
].join(', ');

function isInteractiveRowTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_ROW_TARGET_SELECTOR) !== null;
}

function getInitials(nombre: string | null | undefined): string {
  const words = nombre?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return '#';
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function VisitasTable() {
  const t = useT();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [idCliente, setIdCliente] = useState<string>('');
  const [idUsuario, setIdUsuario] = useState<string>('');
  const [resultado, setResultado] = useState<ResultadoVisita | undefined>(undefined);
  const [fechaDesde, setFechaDesde] = useState<string | undefined>(undefined);
  const [fechaHasta, setFechaHasta] = useState<string | undefined>(undefined);
  const { page, pageSize, sort, handleChange, setPage } =
    useServerPaginacion<VisitaCobroResponse>(20);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useVisitasList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    idCliente: idCliente ? Number(idCliente) : undefined,
    idUsuario: idUsuario || undefined,
    resultado,
    fechaDesde,
    fechaHasta,
    ...sort,
  });

  const { data: stats, isLoading: statsLoading } = useVisitasStats({
    idCliente: idCliente ? Number(idCliente) : undefined,
    idUsuario: idUsuario || undefined,
    resultado,
    fechaDesde,
    fechaHasta,
  });

  const { data: usuarios } = useUsersList({ limit: 1000, offset: 0 });
  const canCreate = useHasPermission(Permission.VISITAS_CREATE);

  const hasFilters =
    idCliente !== '' ||
    idUsuario !== '' ||
    resultado !== undefined ||
    fechaDesde !== undefined ||
    fechaHasta !== undefined;
  const caption = hasFilters ? t('common.segunFiltros') : undefined;

  // KPIs con valor 0 no aportan (ej. nadie registró visitas PAGO); se ocultan
  // del grid pero el Total siempre se muestra.
  const kpisPorResultado = RESULTADO_VISITA.filter((r) => (stats?.porResultado[r] ?? 0) > 0);
  const openClientPayments = (record: VisitaCobroResponse) =>
    navigate(`/clientes/${record.idCliente}/${CLIENTE_TABS.PAGOS}`);
  const tableThemeVars = {
    '--visitas-table-row-hover-bg': token.colorFillTertiary,
    '--visitas-table-row-focus-outline': token.colorPrimary,
  } as CSSProperties;

  if (error) {
    return <EmptyState title={t('pages.visitas.errorLoad')} />;
  }

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id' as keyof VisitaCobroResponse,
      key: 'id',
      ...serverSortProps('id', sort),
      width: 80,
    },
    {
      title: t('pages.visitas.cliente'),
      key: 'cliente',
      ...serverSortProps('cliente', sort),
      render: (_: unknown, record: VisitaCobroResponse) => (
        <span className="visitas-table__client">
          <Avatar
            size={24}
            style={{
              background: token.colorPrimary,
              verticalAlign: 'middle',
              fontSize: 12,
            }}
            aria-hidden
          >
            {getInitials(record.cliente?.nombre)}
          </Avatar>
          <span className="visitas-table__client-content">
            <span className="visitas-table__client-identity">
              {record.cliente
                ? `${record.cliente.nombre ?? ''} (#${record.cliente.id})`
                : `#${record.idCliente}`}
            </span>
            <span className="visitas-table__client-action">
              <ArrowRightOutlined aria-hidden />
              {t('pages.visitas.viewPayments')}
            </span>
          </span>
        </span>
      ),
    },
    {
      title: t('pages.visitas.fecha'),
      dataIndex: 'fecha' as keyof VisitaCobroResponse,
      key: 'fecha',
      ...serverSortProps('fecha', sort),
      render: (val: DateOnlyString) =>
        val ? (parseDateOnly(val)?.format(DATE_FORMAT) ?? '-') : '-',
    },
    {
      title: t('pages.visitas.resultado'),
      dataIndex: 'resultado' as keyof VisitaCobroResponse,
      key: 'resultado',
      ...serverSortProps('resultado', sort),
      render: (val: ResultadoVisita) => <VisitaResultadoTag resultado={val} />,
    },
    {
      title: t('pages.visitas.comentario'),
      dataIndex: 'comentario' as keyof VisitaCobroResponse,
      key: 'comentario',
      ellipsis: true,
      render: (val: string | null) => (val ? val : <Text type="secondary">—</Text>),
    },
    {
      title: t('pages.visitas.usuario'),
      key: 'usuario',
      ...serverSortProps('usuario', sort),
      render: (_: unknown, record: VisitaCobroResponse) =>
        record.usuario ? (record.usuario.username ?? record.usuario.email) : record.idUsuario,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <VisitasKpiSummary
          stats={stats}
          isLoading={statsLoading}
          resultKeys={kpisPorResultado}
          caption={caption}
          translate={t}
        />
      </div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]} className="table-filters">
          <Input
            placeholder={t('pages.visitas.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={idCliente}
            onChange={(e) => {
              setIdCliente(e.target.value);
              setPage(1);
            }}
            style={{ width: 150 }}
            allowClear
          />
          <Select
            placeholder={t('pages.visitas.userPlaceholder')}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            value={idUsuario || undefined}
            onChange={(val) => {
              setIdUsuario(val ?? '');
              setPage(1);
            }}
            options={(usuarios?.data ?? []).map((u) => ({
              label: u.username ?? u.email,
              value: u.id,
            }))}
          />
          <Select
            placeholder={t('pages.visitas.filtroResultado')}
            allowClear
            style={{ width: 180 }}
            value={resultado}
            onChange={(val) => {
              setResultado(val);
              setPage(1);
            }}
            options={RESULTADO_VISITA.map((r) => ({
              value: r,
              label: t(`pages.visitas.resultados.${r}`),
            }))}
          />
          <DateRangeFilter
            value={
              fechaDesde !== undefined || fechaHasta !== undefined
                ? [fechaDesde ?? null, fechaHasta ?? null]
                : null
            }
            onChange={(range) => {
              setFechaDesde(range?.[0] ?? undefined);
              setFechaHasta(range?.[1] ?? undefined);
              setPage(1);
            }}
          />
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('pages.visitas.create')}
          </Button>
        )}
      </div>
      <div className="visitas-table" style={tableThemeVars}>
        <DataTable<VisitaCobroResponse>
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: (
              <EmptyState
                title={t('pages.visitas.emptyTitle')}
                description={t('pages.visitas.emptyDescription')}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: false,
            showTotal: (total, range) =>
              t('pagination.showTotal', { from: range[0], to: range[1], total }),
          }}
          onChange={handleChange}
          rowClassName={() => 'visitas-table__row'}
          onRow={(record) => ({
            tabIndex: 0,
            onClick: (event) => {
              if (!isInteractiveRowTarget(event.target)) {
                openClientPayments(record);
              }
            },
            onKeyDown: (event) => {
              if (
                isInteractiveRowTarget(event.target) ||
                (event.key !== 'Enter' &&
                  event.key !== ' ' &&
                  event.key !== 'Spacebar' &&
                  event.key !== 'Space')
              ) {
                return;
              }
              event.preventDefault();
              openClientPayments(record);
            },
          })}
        />
      </div>
      <VisitaCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refetch}
      />
    </div>
  );
}
